import { useEffect, useState } from "react";
import { Moon, Zap, CheckCircle2, Youtube, Instagram, Link2 } from "lucide-react";

// TikTok isn't in lucide-react — inline SVG.
function TikTokIcon({ className = "w-5 h-5" }) {
    return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.66a8.16 8.16 0 0 0 4.77 1.52V6.69z"/>
        </svg>
    );
}

const PLATFORMS = [
    { id: "youtube",   label: "YouTube Shorts", brand: "#FF0033", Icon: Youtube,     handle: "@gastonlechat" },
    { id: "tiktok",    label: "TikTok",         brand: "#FFFFFF", Icon: TikTokIcon,  handle: "@gaston.chat" },
    { id: "instagram", label: "Instagram Reels",brand: "#E1306C", Icon: Instagram,   handle: "@gaston.chat" },
];

const T = {
    fr: {
        overline: "AUTOPUBLISH · FEUILLE DE ROUTE",
        tagline_1: "AutoPublish arrive.",
        tagline_2: "Bientôt sur",
        subtitle: "Un parcours OAuth unique est à l'étude pour envoyer tes Shorts vers YouTube, TikTok et Instagram Reels. Shorts Factory ne simule aucune publication : tant que la fonctionnalité n'est pas construite et testée de bout en bout, elle n'est pas incluse dans les offres. En attendant, tu télécharges tes Shorts et tu les publies toi-même.",
        bullets: [
            "Objectif : connexion OAuth officielle (Google, TikTok, Meta)",
            "Objectif : planification cross-plateforme depuis un même calendrier",
            "Objectif : statut de publication en direct",
            "Fonctionnalité en développement — pas incluse dans les offres actuelles",
        ],
        cta: "Me prévenir à l'ouverture",
        badge_asleep: "APERÇU · TON WORKFLOW ACTUEL",
        badge_publishing: "APERÇU · AUTOPUBLISH BIENTÔT",
        connected: "Compte à connecter",
        scheduled: "Aperçu planning",
        live: "Aperçu",
    },
    en: {
        overline: "AUTOPUBLISH · ROADMAP",
        tagline_1: "AutoPublish is coming.",
        tagline_2: "Soon on",
        subtitle: "A single OAuth connection to ship your Shorts to YouTube, TikTok and Instagram Reels is on the roadmap. Shorts Factory does not simulate any publish: as long as the feature isn't built and tested end-to-end, it isn't bundled with any plan. Until then you download your Shorts and post them yourself.",
        bullets: [
            "Goal: official OAuth connect (Google, TikTok, Meta)",
            "Goal: cross-platform scheduling from one calendar",
            "Goal: live publish status",
            "Feature in development — not included in current plans",
        ],
        cta: "Notify me at launch",
        badge_asleep: "PREVIEW · YOUR CURRENT WORKFLOW",
        badge_publishing: "PREVIEW · AUTOPUBLISH SOON",
        connected: "Account to connect",
        scheduled: "Schedule preview",
        live: "Preview",
    },
};

export default function AutoPublishSection({ lang = "fr", onJoin }) {
    const t = T[lang] || T.fr;
    // Cycle "publishing" pulse across the 3 platforms
    const [active, setActive] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setActive((n) => (n + 1) % PLATFORMS.length), 1800);
        return () => clearInterval(id);
    }, []);

    return (
        <section id="autopublish" data-testid="autopublish-section" className="relative py-24 sm:py-28 overflow-hidden border-y border-[#253046]">
            {/* Deep night background */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#050914] via-[#080B10] to-[#050914]" />
            <div className="absolute inset-0 studio-grid opacity-20" />
            {/* Two glow orbs */}
            <div className="absolute top-1/2 -left-24 w-96 h-96 rounded-full bg-[#0099FF]/10 blur-3xl" />
            <div className="absolute top-1/3 -right-24 w-96 h-96 rounded-full bg-[#FF4D00]/10 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    {/* Copy */}
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF4D00]/30 bg-[#FF4D00]/10">
                            <Zap className="w-3 h-3 text-[#FF4D00]" fill="currentColor" />
                            <span className="overline text-[#FF4D00]">{t.overline}</span>
                        </div>

                        <h2 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.9] tracking-tight">
                            <span className="flex items-center gap-3 flex-wrap">
                                <Moon className="w-10 h-10 sm:w-14 sm:h-14 text-[#0099FF]" strokeWidth={1.5} />
                                <span>{t.tagline_1}</span>
                            </span>
                            <span className="block mt-2 text-slate-100">{t.tagline_2}</span>
                            <span className="block mt-3 flex flex-wrap items-center gap-3">
                                <span className="flame-text">YouTube</span>
                                <span className="text-slate-500 font-black">·</span>
                                <span className="flame-text">TikTok</span>
                                <span className="text-slate-500 font-black">·</span>
                                <span className="flame-text">Instagram.</span>
                            </span>
                        </h2>

                        <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
                            {t.subtitle}
                        </p>

                        <ul className="mt-8 space-y-3">
                            {t.bullets.map((b, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                                    <span className="text-sm sm:text-base text-slate-200 leading-relaxed">{b}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-9">
                            <button
                                data-testid="autopublish-cta"
                                onClick={onJoin || (() => document.getElementById("join")?.scrollIntoView({ behavior: "smooth" }))}
                                className="inline-flex items-center gap-2 flame-gradient text-black font-semibold px-6 py-3.5 rounded-md kinetic flame-glow"
                            >
                                <Link2 className="w-4 h-4" /> {t.cta}
                            </button>
                        </div>
                    </div>

                    {/* Visual — moon/phones cluster */}
                    <div className="lg:col-span-5 relative">
                        {/* Sleeping user badge */}
                        <div className="mb-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0F141C] border border-[#253046] w-fit">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#0099FF] opacity-70" />
                            <span className="font-mono-sf text-[10px] text-slate-400 uppercase tracking-widest">{t.badge_asleep}</span>
                        </div>
                        <div className="mb-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#161D2A] border border-[#FF4D00]/40 w-fit">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D00] rec-dot" />
                            <span className="font-mono-sf text-[10px] text-[#FF4D00] uppercase tracking-widest">{t.badge_publishing}</span>
                        </div>

                        {/* Platform stack */}
                        <div className="space-y-3">
                            {PLATFORMS.map((p, i) => {
                                const Icon = p.Icon;
                                const isActive = active === i;
                                return (
                                    <div
                                        key={p.id}
                                        data-testid={`autopublish-platform-${p.id}`}
                                        className={`relative rounded-lg border transition-all duration-500 ${isActive ? "border-[#FF4D00] bg-gradient-to-r from-[#1E2738] to-[#161D2A] flame-glow" : "border-[#253046] bg-[#161D2A]"} p-4 flex items-center gap-4 overflow-hidden`}
                                    >
                                        {/* Publishing sweep */}
                                        {isActive && (
                                            <div className="absolute inset-y-0 left-0 w-full flame-gradient opacity-10 animate-pulse pointer-events-none" />
                                        )}
                                        <div className="relative shrink-0 w-11 h-11 rounded-lg grid place-items-center border" style={{ background: p.id === "tiktok" ? "#000" : `${p.brand}22`, borderColor: `${p.brand}44` }}>
                                            <Icon className="w-5 h-5" style={{ color: p.brand }} />
                                        </div>
                                        <div className="relative flex-1 min-w-0">
                                            <div className="font-display uppercase font-bold text-sm sm:text-base">{p.label}</div>
                                            <div className="text-xs text-slate-500 font-mono-sf mt-0.5 truncate">{p.handle}</div>
                                        </div>
                                        <div className="relative shrink-0 text-right">
                                            {isActive ? (
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D00] rec-dot" />
                                                    <span className="font-mono-sf text-[10px] text-[#FF4D00] uppercase tracking-widest">Uploading</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1.5">
                                                    <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />
                                                    <span className="font-mono-sf text-[10px] text-[#10B981] uppercase tracking-widest">{i < active ? t.live : t.scheduled}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer stat */}
                        <div className="mt-6 pt-6 border-t border-[#253046] grid grid-cols-3 gap-2 text-center">
                            <div>
                                <div className="font-display text-2xl font-black flame-text">3</div>
                                <div className="text-[10px] font-mono-sf uppercase text-slate-500 tracking-widest">{lang === "fr" ? "plateformes" : "platforms"}</div>
                            </div>
                            <div>
                                <div className="font-display text-2xl font-black flame-text">1</div>
                                <div className="text-[10px] font-mono-sf uppercase text-slate-500 tracking-widest">{lang === "fr" ? "connexion" : "connection"}</div>
                            </div>
                            <div>
                                <div className="font-display text-2xl font-black flame-text">0</div>
                                <div className="text-[10px] font-mono-sf uppercase text-slate-500 tracking-widest">{lang === "fr" ? "clic manuel" : "manual click"}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
